// Where a widget someone adds goes on a doc board: inside the open page, after the cursor's block (or the
// block the slash menu was opened on), or at the end, the widget and the page's new body in one step. The
// gallery's Add, Connect's and every other add come through here (src/state/actions.ts addWidget).

import { DOC_WIDGET_W, widgetIds, withWidget, withoutWidget } from "../../../shared/docblocks";
import type { Board, ID, Op, WidgetItem } from "../../../shared/types";
import { commit, undo } from "../../state/actions";
import { boardEntries, get, set, toast } from "../../state/store";
import type { Placement } from "../kinds";
import { liveEditor } from "./live";
import { openOn, useDocUi } from "./state";

export function placeInPage(item: WidgetItem, board: Board): Placement {
  const pages = boardEntries(get(), board.id).filter((e) => e.t === "page");
  const page = openOn(pages, useDocUi.getState().open[board.id]);
  if (!page) return { refused: { title: "Make a page first", body: "A widget goes inside a page. Nothing was added." } };
  // in a page a widget is as wide as the page's column; its height is its own
  const sized: WidgetItem = { ...item, x: 0, y: 0, w: DOC_WIDGET_W };
  const live = liveEditor();
  const body = live && live.boardId === board.id && live.pageId === page.id ? live.placeWidget(item.id) : withWidget(page.data.body, item.id);
  const ops: Op[] = [
    { op: "item.put", item: sized },
    { op: "entry.put", entry: { ...page, data: { ...page.data, body } } },
  ];
  return { ops };
}

/**
 * A widget in a page, removed from its own menu: its block goes with it, in one step. The open page's editor
 * does it (as if the block were deleted there); a page not open is changed as it is stored.
 */
export function removeFromPage(itemId: ID) {
  const item = get().items[itemId];
  if (!item) return;
  set({ selection: [] });
  if (liveEditor()?.removeWidget(itemId)) return;
  const page = boardEntries(get(), item.boardId).find((e) => e.t === "page" && widgetIds(e.data.body).includes(itemId));
  const ops: Op[] = [{ op: "item.del", id: itemId }];
  if (page) ops.push({ op: "entry.put", entry: { ...page, data: { ...page.data, body: withoutWidget(page.data.body, itemId) } } });
  commit(ops, "Remove widget");
  toast({ level: "info", title: `Removed “${item.kind === "widget" ? item.title || "the widget" : "the widget"}”`, body: "It went to the trash with its block.", action: { label: "Undo", run: undo } });
}
